import React, { useEffect, useMemo, useRef, useState } from "react";
import { Loader2, Search } from "lucide-react";
import api from "@/lib/api";
import FilterBar from "@/components/FilterBar";
import OpportunityCard from "@/components/OpportunityCard";

// Chrome-free embed page for WordPress / school website iframe embeds.
// - No header/footer
// - Transparent background (so the parent page bg shows through)
// - Sends its height to the parent via postMessage so the iframe can auto-resize.
export default function EmbedBoard() {
  const [opps, setOpps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [type, setType] = useState("");
  const [year, setYear] = useState("");
  const [query, setQuery] = useState("");
  const containerRef = useRef(null);

  useEffect(() => {
    let cancel = false;
    const load = async () => {
      setLoading(true);
      try {
        const params = {};
        if (type) params.type = type;
        if (year) params.year_group = year;
        const { data } = await api.get("/public/opportunities", { params });
        if (!cancel) setOpps(data);
      } catch (_e) {
        if (!cancel) setOpps([]);
      } finally {
        if (!cancel) setLoading(false);
      }
    };
    load();
    return () => { cancel = true; };
  }, [type, year]);

  // Tell the parent window what height we need so the iframe can resize.
  useEffect(() => {
    const send = () => {
      const h = containerRef.current?.scrollHeight || document.body.scrollHeight;
      try {
        window.parent.postMessage({ type: "careers-hub-height", height: h }, "*");
      } catch (_e) { /* ignore */ }
    };
    send();
    const ro = new ResizeObserver(send);
    if (containerRef.current) ro.observe(containerRef.current);
    window.addEventListener("load", send);
    return () => {
      ro.disconnect();
      window.removeEventListener("load", send);
    };
  }, [opps, loading, query]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return opps;
    return opps.filter((o) =>
      [o.title, o.description, o.provider, o.location].some((s) => s && s.toLowerCase().includes(q))
    );
  }, [opps, query]);

  return (
    <div ref={containerRef} className="px-4 sm:px-6 py-6 bg-transparent" data-testid="embed-board">
      <div className="mb-5">
        <div className="relative max-w-xl">
          <Search className="h-4 w-4 absolute left-4 top-1/2 -translate-y-1/2 text-zinc-500" />
          <input
            type="text"
            placeholder="Search opportunities..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="brut-input pl-11"
            data-testid="embed-search"
          />
        </div>
      </div>

      <div className="mb-6">
        <FilterBar type={type} setType={setType} year={year} setYear={setYear} />
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12" data-testid="embed-loading">
          <Loader2 className="h-7 w-7 animate-spin text-[#C8102E]" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="brut-card-static p-8 text-center" data-testid="embed-empty">
          <div className="font-display text-xl font-black mb-1">Nothing here yet</div>
          <p className="text-zinc-700 font-medium text-sm">No opportunities match your filters right now.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="embed-grid">
          {filtered.map((o) => (
            <OpportunityCard
              key={o.id}
              opp={o}
              to={`${window.location.origin}/opportunity/${o.id}`}
              external
            />
          ))}
        </div>
      )}

      <div className="mt-6 text-xs text-zinc-500 text-right">
        Powered by Careers Hub
      </div>
    </div>
  );
}
